import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Input, FormField, ToggleGroup } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';

const DEFAULT_STATS = [
  { value: '+125', label: 'clients exportateurs' },
  { value: '8 200', label: 'shipments / an' },
  { value: '96%', label: 'ponctualité 2025' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    api.get('/portal-settings/login-stats')
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setStats(r.data); })
      .catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t('login.invalid_credentials');
      setError(typeof msg === 'string' ? msg : t('login.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-10 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-100px] left-[-100px] w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-[-80px] right-[-80px] w-80 h-80 rounded-full bg-accent/20 blur-3xl" />
          <div className="absolute top-1/2 right-[-60px] w-56 h-56 rounded-full bg-white/5 blur-2xl" />
        </div>

        {/* Logo + brand */}
        <div className="relative flex items-center gap-3">
          <div className="w-16 h-15 rounded-lg bg-white flex items-center justify-center overflow-hidden">
            <img
              src="/mfb-logo.png"
              alt="MFB"
              className="w-20 h-12 object-contain mb-1"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement.innerHTML =
                  '<span class="text-accent font-black text-sm">MFB</span>';
              }}
            />
          </div>
          <div>
            <p className="text-white font-bold text-[15px] leading-tight">{t('brand.name')}</p>
            <p className="text-white/50 text-[11px]">{t('brand.tagline')}</p>
          </div>
        </div>

        {/* Headline */}
        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight text-white mb-4">
            {i18n.language === 'fr'
              ? <>Votre supply chain F&amp;L,{' '}<span className="text-accent">de la palette au consommateur.</span></>
              : <>Your F&amp;V supply chain,{' '}<span className="text-accent">from pallet to consumer.</span></>
            }
          </h1>
          <p className="text-white/60 text-[13px] leading-relaxed max-w-sm">
            {t('login.subheadline')}
          </p>

          {/* Stats */}
          <div className="mt-10 flex gap-8">
            {stats.map((s, i) => (
              <div key={i}>
                <p className="text-white font-bold text-xl">{s.value}</p>
                <p className="text-white/50 text-[11px] mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="relative text-white/30 text-[11px]">{t('brand.footer')}</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Lang toggle */}
        <div className="flex justify-end p-6">
          <ToggleGroup
            options={[{ value: 'fr', label: 'FR' }, { value: 'en', label: 'EN' }]}
            value={i18n.language === 'en' ? 'en' : 'fr'}
            onChange={(lang) => i18n.changeLanguage(lang)}
          />
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">{t('login.welcome')}</h2>
              <p className="text-[13px] text-slate-500 mt-1">{t('login.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label={t('login.email_label')} required>
                <Input
                  type="email"
                  id="email"
                  placeholder={t('login.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </FormField>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                    {t('login.password_label')} <span className="text-rose-500">*</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-[11.5px] text-primary hover:underline"
                  >
                    {t('login.forgot_password')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <EyeOff size={14} strokeWidth={1.75} />
                      : <Eye size={14} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                iconRight={!loading ? ArrowRight : undefined}
                className="w-full mt-2"
              >
                {t('login.submit')}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] text-slate-400 font-medium">{t('common.or')}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Legal */}
            <p className="text-center text-[11px] text-slate-400 mt-8">
              {t('login.legal')}{' '}
              <Link to="/legal/cgu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t('login.cgu')}</Link>
              {' '}{t('login.and_our')}{' '}
              <Link to="/legal/confidentialite" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t('login.privacy_policy')}</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
